/**
 * @file AnimationTick
 * @author Yu Zong(liuxuanzy@qq.com)
 */
import bezier from 'bezier-easing';
import { colorEvaluate } from './ColorParser';
var TIMING = {
    linear: function (t) { return t; },
    ease: bezier(0.25, 0.1, 0.25, 1),
    'ease-in': bezier(0.42, 0, 1, 1),
    'ease-out': bezier(0, 0, 0.58, 1),
    'ease-in-out': bezier(0.42, 0, 0.58, 1),
    // todo 暂时不支持自定义
    bezier: bezier(0.42, 0, 1, 1),
    'ease-out-back': function (x) {
        // 生成工具：https://www.wolframalpha.com/input/?i=f%280%29+%3D+0%3B+f%27%280%29+%3D+0%3Bf%27%27%28t%29+%3D+-135%28f%28t%29+-+1%29+-+12f%27%28t%29
        return -(2 * Math.pow(Math.E, -6 * x) * Math.sin(3 * Math.sqrt(11) * x) / Math.sqrt(11)) - (Math.pow(Math.E, -6 * x) * Math.cos(3 * Math.sqrt(11) * x)) + 1;
    },
};
export var MeasureType;
(function (MeasureType) {
    MeasureType[MeasureType["ZERO"] = 0] = "ZERO";
    MeasureType[MeasureType["PX"] = 1] = "PX";
    MeasureType[MeasureType["PERCENTAGE"] = 2] = "PERCENTAGE";
    MeasureType[MeasureType["NONE"] = 3] = "NONE";
})(MeasureType || (MeasureType = {}));
export function getMeasure(value) {
    if (typeof value === 'number') {
        return [value, value === 0 ? MeasureType.ZERO : MeasureType.NONE];
    }
    if (/(px|%)$/i.test(value)) {
        var num = Number(value.slice(0, RegExp.$1.length));
        if (!isFinite(num)) {
            throw new Error("invalid value = ".concat(value));
        }
        if (num === 0) {
            return [0, MeasureType.ZERO];
        }
        return [num, RegExp.$1 === '%' ? MeasureType.PERCENTAGE : MeasureType.PX];
    }
    throw new Error("invalid value = ".concat(value));
}
function formatMeasure(num, measure) {
    if (measure === MeasureType.ZERO || measure === MeasureType.NONE) {
        return num;
    }
    return "".concat(num).concat(measure === MeasureType.PX ? 'px' : '%');
}
function ensureMeasure(start, end) {
    if (start !== MeasureType.ZERO && end !== MeasureType.ZERO) {
        if (start !== end) {
            throw new Error('measure is not same');
        }
        return start;
    }
    if (start !== MeasureType.ZERO) {
        return start;
    }
    if (end !== MeasureType.ZERO) {
        return end;
    }
    return MeasureType.ZERO;
}
var AnimationTick = /** @class */ (function () {
    function AnimationTick(animations, isColor) {
        var _a = this.calcAnimation(animations), range = _a[0], totalTime = _a[1], startValue = _a[2];
        this.range = range;
        this.totalTime = totalTime;
        this.startValue = startValue;
        this.isColor = isColor;
    }
    AnimationTick.prototype.getTotalTime = function () {
        return this.totalTime;
    };
    AnimationTick.prototype.getStartValue = function () {
        return this.startValue;
    };
    AnimationTick.prototype.tick = function (timeSpan) {
        if (timeSpan === 0) {
            return this.startValue;
        }
        for (var _i = 0, _a = this.range; _i < _a.length; _i++) {
            var item = _a[_i];
            var startValue = item.startValue, endTime = item.endTime, duration = item.duration, endValue = item.endValue, startTime = item.startTime, timingFunction = item.timingFunction;
            if (startTime < timeSpan && endTime >= timeSpan) {
                if (startValue === endValue) {
                    return endValue;
                }
                var t = TIMING[timingFunction]((timeSpan - startTime) / duration);
                if (this.isColor) {
                    return colorEvaluate(t, String(startValue), String(endValue));
                }
                var _b = getMeasure(startValue), start = _b[0], startMeasure = _b[1];
                var _c = getMeasure(endValue), to = _c[0], toMeasure = _c[1];
                var measure = ensureMeasure(startMeasure, toMeasure);
                return formatMeasure(start + (to - start) * t, measure);
            }
        }
        throw new Error("invalid timeSpan = ".concat(timeSpan, ", total range = ").concat(this.totalTime));
    };
    AnimationTick.prototype.calcAnimation = function (animations) {
        var startValue = animations[0].startValue;
        var ranges = [];
        var total = 0;
        var lastValue = startValue;
        for (var _i = 0, animations_1 = animations; _i < animations_1.length; _i++) {
            var item = animations_1[_i];
            if (item.delay) {
                ranges.push({
                    startTime: total,
                    duration: item.delay,
                    endTime: total + item.delay,
                    startValue: lastValue,
                    endValue: lastValue,
                    timingFunction: 'ease',
                });
                total += item.delay;
            }
            ranges.push({
                startTime: total,
                endTime: total + item.duration,
                startValue: item.startValue,
                endValue: item.toValue,
                duration: item.duration,
                timingFunction: item.timingFunction || 'ease',
            });
            lastValue = item.toValue;
            total += item.duration;
        }
        return [ranges, total, startValue];
    };
    return AnimationTick;
}());
export { AnimationTick };
